/*
*   Seperate context for handling dev plan content/added LID.
*/

import React, { createContext, useContext, useState, useEffect } from "react";
import { useMapContext } from "./MapContext";
import {RG_COST_RATE, GR_COST_RATE, PLANTER_COST_RATE, RB_COST_RATE} from "../features/LID/LIDCosts";

const LIDContext = createContext();

export const LIDProvider = ({ children }) => {
  const {
    userData,
    devPlanID,
    startOperation,
    finishOperation,
  } = useMapContext();

  const [totalCost, setTotalCost] = useState(0);
  const [devPlanContent, setDevPlanContent] = useState({});
  const [aggregatedCounts, setAggregatedCounts] = useState({});
  const [brushedSubcatchments, setBrushedSubcatchments] = useState(new Set()); // store IDs of the selected subcatchments with the brush tool
  const [brushedFeaturesData, setBrushedFeaturesData] = useState({});

  // Fetch dev plan content when the state changes
  useEffect(() => {
    if (userData && devPlanID) {
       // setDevPlanContent({}); // Remove content when updating
      let inpFile = `GLWA_Aug16_${userData.id}_${devPlanID}`;

      const fetchDevPlanContent = async (inpFile) => {
        try {
          const response = await fetch(`https://2471d2z813.execute-api.us-east-2.amazonaws.com/read-lid/${inpFile}`);
          if (response.ok) {
            const data = await response.json();
            if (!data.message) {
              return {'data': data, 'exists': true};
            } else {
              return {'data': null, 'exists': false};
            }
          } else {
            return {'data': null, 'exists': false};
          }
        } catch (error) {
          console.log("Error fetching LID content:", error);
        }
      }

      const checkForDevPlan = async () => {
        try {
          startOperation();
          let devPlanExists = false;
          const result = await fetchDevPlanContent(inpFile);
          if (result.exists) {
            if (result.data.hasOwnProperty('lid_usage')) {
              setDevPlanContent(result.data.lid_usage);
              console.log("Dev plan content:", result.data.lid_usage);
            } else {
              setDevPlanContent(result.data);
              console.log("Dev plan content:", result.data);
            }
            devPlanExists = true;

          }

          if (!devPlanExists) {
            setDevPlanContent({});
            console.log("Dev plan content:", {});
          }
          if (`GLWA_Aug16_${userData.id}_${devPlanID}` !== inpFile) {
            setDevPlanContent({});
            inpFile = `GLWA_Aug16_${userData.id}_${devPlanID}`;
            checkForDevPlan();
          }
        }
        finally {
          finishOperation();
        }

      }
      checkForDevPlan();

    }
  }, [devPlanID, userData]);


  // Sum the total cost of the LID
  useEffect(() => {
    const updateCost = () => {
      let totalCost = 0;
      for (const subcatchmentName in devPlanContent) {
        if (devPlanContent[subcatchmentName] === undefined) continue;
        if (devPlanContent[subcatchmentName]["RG1"]) {
          totalCost += devPlanContent[subcatchmentName]["RG1"].n_replicate * devPlanContent[subcatchmentName]["RG1"].area * (RG_COST_RATE);
        }
        if (devPlanContent[subcatchmentName]["GR1"]) {
          totalCost += devPlanContent[subcatchmentName]["GR1"].n_replicate * devPlanContent[subcatchmentName]["GR1"].area * (GR_COST_RATE);
        }
        if (devPlanContent[subcatchmentName]["Planter"]) {
          totalCost += devPlanContent[subcatchmentName]["Planter"].n_replicate * devPlanContent[subcatchmentName]["Planter"].area * (PLANTER_COST_RATE);
        }
        if (devPlanContent[subcatchmentName]["RB12"]) {
          let vol = ((devPlanContent[subcatchmentName]["RB12"].area / 0.133681) * 3).toFixed(2);
          totalCost += devPlanContent[subcatchmentName]["RB12"].n_replicate * vol * (RB_COST_RATE);
        }
      }
      setTotalCost(totalCost);
    }
    updateCost();
  }, [devPlanContent]);

  // This is here for the LID panel that is displayed when a model has been run and LID can't be added anymore
  const aggregateLIDCounts = (LIDContent) => {
    const totalCounts = {};
    Object.values(LIDContent).forEach(subcatchment => {
      Object.entries(subcatchment).forEach(([lidType, data]) => {
        const count = parseInt(data.n_replicate, 10);
        if (totalCounts[lidType]) {
          totalCounts[lidType] += count;
        } else {
          totalCounts[lidType] = count;
        }
      });
    });

    return totalCounts;
  };

  useEffect(() => {
    if (devPlanContent) {
      setAggregatedCounts(aggregateLIDCounts(devPlanContent));
    }
    console.log("The aggregatedCounts are: ", aggregatedCounts);
  }, [devPlanContent, devPlanID]);



  const value = {
    devPlanContent,
    setDevPlanContent,
    totalCost,
    aggregatedCounts,
    brushedSubcatchments,
    setBrushedSubcatchments,
    brushedFeaturesData,
    setBrushedFeaturesData,
  }

  return (
    <LIDContext.Provider value={value}>
      {children}
    </LIDContext.Provider>
  );
};

export const useLIDContext = () => {
  const context = useContext(LIDContext);
  if (!context) {
    throw new Error("useLIDContext must be used within a LIDProvider");
  }
  return context;
}